/**
 * UiMode Context - Resolves tenant ui_mode for mode-based rendering.
 *
 * Reads `tenant.ui_mode` from TenantProvider and exposes:
 * - effectiveMode: resolved UiMode ('default' | 'employee')
 * - loading: true until tenant fetch resolves (prevents VoiceInterface race)
 */

import { createContext, useContext, ReactNode } from 'react';
import { useTenant } from './TenantContext';
import type { UiMode } from '../modes/types';

interface UiModeContextType {
  effectiveMode: UiMode;
  loading: boolean;
}

const UiModeContext = createContext<UiModeContextType>({
  effectiveMode: 'default',
  loading: true,
});

export function UiModeProvider({ children }: { children: ReactNode }): JSX.Element {
  const { tenant, loading: tenantLoading } = useTenant();

  const raw = tenant?.ui_mode;
  const effectiveMode: UiMode = (raw === 'employee') ? 'employee' : 'default';

  return (
    <UiModeContext.Provider value={{ effectiveMode, loading: tenantLoading }}>
      {children}
    </UiModeContext.Provider>
  );
}

export function useUiMode(): UiModeContextType {
  const context = useContext(UiModeContext);
  if (context === undefined) {
    throw new Error('useUiMode must be used within a UiModeProvider');
  }
  return context;
}
